import React, { useContext, useState } from 'react';
import Button from '../components/Button';
import TextInput from '../components/Inputs/TextInput';
import useFetch from '../hooks/useFetch';
import config from '../config';
import Axios from 'axios';
import { checkFileType, cloudServiceAttachmentLabels, cloudServices, defaultDocketTemp, getCurrDate } from '../helpers';
import { docketTemp } from '../templates/templates';
import { v4 as uuidv4 } from 'uuid';

export default function AddDocket() {
	const [orderUuid, setOrderUuid] = useState('');

	const { fetch } = useFetch();
	const CHUNK_SIZE = 100 * 1000;

	async function createImgUrls(files) {
		if (!files || files.length === 0) return [];

		let imgs = [];
		for (let i = 0; i < files.length; i++) {
			if (checkFileType(files[i]?.filename?.split('.')[1]) === 1) {
				if (
					files[i].originalname?.includes('fdi-iso-3950') ||
					files[i].originalname?.includes('palmernotation') ||
					files[i].originalname?.includes('uns') ||
					files[i].originalname?.includes('QR_code')
				) {
					if (files[i]?.filename?.includes('file-dupe-')) {
						continue;
					}
					try {
						const res = await Axios({
							url: `${config.api}/file?path=${files[i].path}`,
							method: 'GET',
							responseType: 'blob',
						});

						if (res.data) {
							const url = window.URL.createObjectURL(new Blob([res.data]));
							let file = files[i];
							file.url = url;
							imgs.push(file);
						}
					} catch (err) {
						// swallow, admin action
					}
				}
			}
		}
		return imgs;
	}

	function parseDocketFields(docket) {
		let parsedFields = {};
		for (let i = 0; i < docket?.fields?.length; i++) {
			if (docket?.fields[i]?.data?.fieldName) {
				parsedFields[docket?.fields[i]?.data?.fieldName] = {
					fieldText: docket?.fields[i]?.data?.fieldText,
				};
			}
		}
		return parsedFields;
	}

	// ---- NEW: normalise order.customFields to the shape docketTemp expects ----
	function normalizeCustomFields(customFields) {
		if (!customFields || typeof customFields !== 'object') return {};

		const hasWrapperKeys =
			'fields' in customFields ||
			'includedFields' in customFields ||
			'included_fields' in customFields ||
			'docketName' in customFields ||
			'docketUuid' in customFields;

		// Legacy format: already a flat map (fieldName -> { value, ... } or primitive)
		if (!hasWrapperKeys) {
			return customFields;
		}

		const fields = { ...(customFields.fields || {}) };

		// Merge includedFields / included_fields into the same map
		const included = customFields.includedFields || customFields.included_fields || {};
		Object.entries(included).forEach(([key, value]) => {
			if (!value) return;
			if (typeof value === 'object' && 'value' in value) {
				fields[key] = value;
			} else {
				fields[key] = { value };
			}
		});

		// Ensure daterequired is available as a normal field
		if (customFields.daterequired && (!fields.daterequired || fields.daterequired.value == null)) {
			fields.daterequired = { value: customFields.daterequired };
		}

		return fields;
	}

	const uploadChunk = async ({ chunk, index, filename, chunksDirUuid, teamUuid }) => {
		const formData = new FormData();
		formData.append('teamUuid', teamUuid);
		formData.append('fileAttachmentType', 1);
		formData.append('chunkIndex', index);
		formData.append('filename', filename);
		formData.append('chunksDirUuid', chunksDirUuid);
		formData.append('chunk', chunk);

		await Axios.post(`${config.apiv1}/file/chunk.create`, formData, {
			headers: { 'Content-Type': 'multipart/form-data' },
		});
	};

	async function postFiles({ newFiles, orderUuid, ignoreHook, teamUuid, sellerTeamUuid }) {
		let uploadedChunksTotal = 0;

		for (let i = 0; i < newFiles?.length; i++) {
			if (!newFiles[i]) return;

			const chunksDirUuid = uuidv4();
			const file = newFiles[i];
			const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
			const filename = file.name?.split('.')[0];
			const mimetype = file.name?.split('.')[1];

			for (let j = 0; j < totalChunks; j++) {
				const start = j * CHUNK_SIZE;
				const end = Math.min(start + CHUNK_SIZE, file.size);
				const chunk = file.slice(start, end);

				await uploadChunk({ chunk, index: j, filename, chunksDirUuid, teamUuid });
				uploadedChunksTotal += 1;
			}
			await Axios.post(`${config.apiv1}/file/chunks.merge`, {
				filename,
				totalChunks,
				chunksDirUuid,
				mimetype,
				orderUuid,
				ignoreHook,
				fileAttachmentType: 1,
				teamUuid: teamUuid,
				dupeTeamUuid: sellerTeamUuid,
				originalname: file.name,
				size: file.size,
			});
		}
		return;
	}

	async function generateAndPostDocket() {
		if (!orderUuid) return;

		const order = await fetch(`${config.apiv1}/order/order.read/${orderUuid}`);
		const buyerTeam = await fetch(`${config.apiv1}/team/team.read/${order.buyerTeamUuid}`);
		let orderExtFiles = await fetch(
			`${config.apiv1}/externalAttachment/externalAttachment.read/order/${orderUuid}`
		);
		let dockets = await fetch(`${config.apiv1}/docket/dockets.read/${order.sellerTeamUuid}`);

		if (!dockets || dockets.length === 0) {
			dockets = [defaultDocketTemp(buyerTeam)];
		}

		// Attach service metadata to external attachments
		for (let service of Object.values(cloudServices)) {
			for (let i = 0; i < orderExtFiles.length; i++) {
				if (orderExtFiles[i]?.serviceUuid === service.serviceUuid) {
					orderExtFiles[i].service = service;
				}
			}
		}

		// Flatten external files to the shape docketTemp expects
		for (let i = 0; i < orderExtFiles.length; i++) {
			orderExtFiles[i] = { ...orderExtFiles[i].fields, service: orderExtFiles[i].service };
		}

		// Only order dockets
		dockets = dockets?.filter((docket) => docket.docketType === 1);
		const index = dockets.findIndex((docket) => docket.docketUuid === order?.customFields?.docketUuid);

		let orderFiles = order.files;
		let toothChart = [];
		const charts = ['fdi-iso-3950', 'palmernotation', 'uns'];

		if (order.files && order.files.length > 0) {
			orderFiles = await createImgUrls(orderFiles);
			orderFiles.forEach((file) => {
				if (
					file.originalname.includes(charts[0]) ||
					file.originalname.includes(charts[1]) ||
					file.originalname.includes(charts[2])
				) {
					const changedFile = { ...file, type: 'image/png' };
					toothChart.push(changedFile);
				}
			});
		}

		// --- NEW: normalize customFields into the fields-map shape for docketTemp ---
		const customFieldsRaw = order.customFields || {};
		const fieldsForTemplate = Object.keys(customFieldsRaw).length > 0 ? normalizeCustomFields(customFieldsRaw) : {};

		const docketPDF = await docketTemp(
			fieldsForTemplate,
			toothChart,
			buyerTeam?.teamInfo?.teamName,
			order,
			orderFiles,
			buyerTeam,
			order?.shippingAddress,
			order?.behalfOf?.name,
			parseDocketFields(dockets[index]),
			false,
			orderExtFiles,
			true
		);

		docketPDF.name = `docket_${getCurrDate()}.pdf`;

		await postFiles({
			newFiles: [docketPDF],
			orderUuid,
			teamUuid: buyerTeam?.teamUuid,
			sellerTeamUuid: order.sellerTeamUuid,
			ignoreHook: order.orderProcessingStatus === 0,
		});
	}

	return (
		<div>
			<TextInput text='Order Uuid' callback={setOrderUuid} />
			<Button
				text='Add docket to order'
				onClick={() => {
					generateAndPostDocket();
				}}
			/>
		</div>
	);
}
